/**
 * Lixeira de condomínios (Fase 2 da exclusão) — tela do DONO.
 * Lista os condomínios na lixeira com os dias restantes até a purga (30 dias),
 * e permite Restaurar ou Apagar de vez (este pede a senha do Dono). Só o Dono.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, RotateCcw, Trash2, Undo2 } from "lucide-react";
import { api, ApiError } from "../lib/api";

interface ItemLixeira {
  id: number;
  nome: string;
  apagar_em: string | null;
  dias_restantes: number;
  apagado_por: string;
}

export function LixeiraPage({ onVoltar }: { onVoltar: () => void }) {
  const qc = useQueryClient();
  const inval = () => {
    qc.invalidateQueries({ queryKey: ["lixeira"] });
    qc.invalidateQueries({ queryKey: ["condominios"] });
  };
  const { data, isLoading } = useQuery<{ itens: ItemLixeira[]; retencao_dias: number }>({
    queryKey: ["lixeira"],
    queryFn: () => api.get("/lgpd/lixeira"),
  });

  const [erro, setErro] = useState<string | null>(null);
  const [apagando, setApagando] = useState<ItemLixeira | null>(null);
  const [senha, setSenha] = useState("");

  const restaurar = useMutation({
    mutationFn: (id: number) => api.post(`/lgpd/lixeira/${id}/restaurar`, {}),
    onSuccess: () => { setErro(null); inval(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível restaurar."),
  });

  const apagarAgora = useMutation({
    mutationFn: (id: number) => api.post(`/lgpd/lixeira/${id}/apagar-agora`, { senha }),
    onSuccess: () => { setErro(null); setApagando(null); setSenha(""); inval(); },
    onError: (e) => setErro(
      e instanceof ApiError
        ? e.message
        : `Não foi possível apagar. ${(e as Error)?.message || ""}`.trim(),
    ),
  });

  const itens = data?.itens ?? [];

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-10 h-10 rounded-xl bg-amber-600 text-white flex items-center justify-center"><Trash2 size={20} /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lixeira</h1>
          <p className="text-sm text-slate-500">
            Condomínios apagados ficam aqui por {data?.retencao_dias ?? 30} dias — dá para
            <b> restaurar</b>. Depois do prazo, somem de vez.
          </p>
        </div>
      </div>

      {erro && <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{erro}</div>}

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> Carregando…
        </div>
      ) : itens.length === 0 ? (
        <div className="mt-6 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-8">
          A lixeira está vazia.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {itens.map((it) => (
            <div key={it.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{it.nome}</div>
                <div className="text-[12px] text-slate-500">
                  Restam <b className={it.dias_restantes <= 3 ? "text-rose-600" : "text-amber-700"}>{it.dias_restantes} dia(s)</b>
                  {it.apagado_por ? ` · apagado por ${it.apagado_por}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => restaurar.mutate(it.id)}
                  disabled={restaurar.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-sm font-medium disabled:opacity-50"
                >
                  <Undo2 size={15} /> Restaurar
                </button>
                <button
                  onClick={() => { setApagando(it); setSenha(""); setErro(null); }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 px-3 py-2 text-sm font-medium"
                >
                  <Trash2 size={15} /> Apagar de vez
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmação de apagar de vez (pede a senha do Dono) */}
      {apagando && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
            <div className="flex items-center gap-2 text-rose-700 mb-2">
              <RotateCcw size={18} /> <h3 className="text-lg font-semibold">Apagar de vez</h3>
            </div>
            <p className="text-sm text-slate-700">
              Isto apaga <b>{apagando.nome}</b> definitivamente (banco + arquivos), sem volta.
              Digite a sua senha (de login) para confirmar.
            </p>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Sua senha"
              className="mt-3 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setApagando(null)} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">Cancelar</button>
              <button
                onClick={() => apagarAgora.mutate(apagando.id)}
                disabled={!senha || apagarAgora.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-sm font-semibold rounded-md"
              >
                {apagarAgora.isPending ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                Apagar definitivamente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
